import React, {useEffect, useMemo, useRef, useState} from 'react';
import {FlexColumnSection} from "../../styles/globalStyles";
import {Spin} from "antd";
import {useSelector, useDispatch} from "react-redux";
import useScreenSize from "../../helpers/hooks/useScreenSize";
import {DB_DATE_FORMAT, LOADING_PAGE, LOADING_PAYMENT} from "@arboxappv4/shared/src/constants/constants";
import dayjs from "dayjs";
import {updateCurrStep, updateError} from "@arboxappv4/shared/src/redux/stepperSlice";
import {t} from "i18next";
import {
    callSiteLog,
    checkLegitURL,
    isILPhone,
    sendMessageToPaymentIframe,
    updatePaymentProcessInDB
} from "../../helpers/functions";
import {
    executionSuccessLog,
    openKioskTokenVerifyModal,
    paymentEnterEventListenerLog,
    paymentErrorResponseLog, paymentGotInitMessageLog,
    paymentIframeLog,
    paymentLogGroup,
    paymentNoInitMessageLog,
    paymentStopListeningResponseLog,
    paymentSubmitLog,
    paymentSuccessResponseLog, paymentTimeoutMessageLog, projectTypeMainSlice, projectTypes, startedExecutionLog
} from "@arboxappv4/shared/src/helpers/constants";
import {
    KIOSK_TOKEN_VERIFY_MODAL, ONE_TIME_PASSWORD_MODAL,
    openModal, updateModalRes
} from "@arboxappv4/shared/src/redux/modalManagerSlice";
import {stepOptions} from "../StepOptions";
import {handlePurchasePixel} from "../FlowOptions";
import {pixelEvents} from "../../helpers/hooks/UsePixel";
import {setLoadingType} from "@arboxappv4/shared/src/redux/siteSlice";
import {setKioskLoadingType} from "@arboxappv4/shared/src/redux/kioskStandAlone/kioskSlice";
import {useSearchParams} from "react-router-dom";

const Payment = (props) => {
    const { values, setFieldValue, handleSubmit, idPropOverride, startOverride, flowType, selectedSeriesEvents } = props;
    const project_type = useSelector(state => state.project?.project_type)
    const box = useSelector(state => state[projectTypeMainSlice[project_type]]?.box)
    const language = useSelector(state => state[projectTypeMainSlice[project_type]]?.language)
    const location = useSelector(state => state[projectTypeMainSlice[project_type]]?.selectedLocation)
    const token = useSelector(state => state.auth?.token)
    const userBox = useSelector(state => state.user?.activeUsersBoxes ?? state.kiosk?.usersBoxes)
    const membershipUser = useSelector(state => state.kiosk?.membershipUser)
    const kioskToken = useSelector(state => state.kiosk?.token)
    const uuid = useSelector(state => state.uuid?.uuid)
    const error = useSelector(state => state.stepper.error)
    const stepperConfig = useSelector(state => state.stepper)
    const kioskTokenVerifyModalRes = useSelector(state => state.modalManager[KIOSK_TOKEN_VERIFY_MODAL].processRes)

    const [loading, setLoading] = useState(true);
    const [iframeSrc, setIframeSrc] = useState(null);
    const [submitted, setSubmitted] = useState(false);
    const [showErrorScreen, setShowErrorScreen] = useState(false);
    const [gotInitMessage, setGotInitMessage] = useState(null);
    const [verifiedUBT, setVerifiedUBT] = useState(false);

    const {isMobile} = useScreenSize()
    const dispatch = useDispatch()
    const gotInitMessageRef = useRef(gotInitMessage);
    const [searchParams] = useSearchParams();

    const hideBack = useMemo(() => (stepperConfig.steps?.length ?? 1) === 1, [stepperConfig.steps])
    const params = useMemo(() => Object.fromEntries([...searchParams]), [searchParams]);
    const isKiosk = useMemo(() => params.kiosk === 'true', [params]);

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            //if gotInitMessage is null after 9 seconds, stop payment process completely
            if(!gotInitMessageRef.current) {
                if(stepperConfig.currStep === stepOptions.PAYMENT) { //we check this to ensure we don't display an error if the user exited this step already (mostly because of sign up steps update)
                    callSiteLog(paymentNoInitMessageLog, {}, paymentLogGroup)
                    dispatch(updateError(t('payment-communication-error')))
                }
            }
        }, 9000)
        const listenToMessages = (event) => {
            console.log('testing if same uuid payment', event, event.data?.data?.uuidPayment, gotInitMessageRef.current?.uuidPayment)
            if(event.data?.data?.uuidPayment === gotInitMessageRef.current?.uuidPayment) {
                if (event.data.type === "PAYMENT_RESPONSE_SUCCESS") {
                    setTimeout(() => {
                        callSiteLog(paymentSuccessResponseLog, event.data.data, paymentLogGroup)
                        setFieldValue('payment_info', event.data.data)
                    }, 10)
                }
                if (event.data.type === "EXECUTION_RESPONSE_SUCCESS") {
                    setTimeout(() => {
                        callSiteLog(executionSuccessLog, event.data.data, paymentLogGroup)
                        if (event.data.data?.payment_info) setFieldValue('payment_info', event.data.data.payment_info)
                        setFieldValue('res', event.data.data.response ?? true)
                    }, 10)
                }
                if (event.data.type === "PAYMENT_RESPONSE_ERROR") {
                    console.log('error!!!! ', event.data)
                    callSiteLog(paymentErrorResponseLog, event.data, paymentLogGroup)
                    dispatch(updateError(event.data?.data?.error?.messageToUser ?? t('payment-generic-error')))
                    dispatch(setLoadingType(null))
                    dispatch(setKioskLoadingType(null))
                }
                if (event.data.type === "PAYMENT_SITE_BACK") {
                    const stepIndex = stepperConfig.steps.indexOf(stepperConfig?.currStep);
                    dispatch(updateCurrStep(stepperConfig.steps[stepIndex - 1]))
                }
                if (event.data.type === 'STARTED_EXECUTION') {
                    callSiteLog(startedExecutionLog, event.data, paymentLogGroup)
                    dispatch(setKioskLoadingType(LOADING_PAYMENT))
                    dispatch(setLoadingType(LOADING_PAYMENT))
                    if (event.data.data?.payment_info) setFieldValue('payment_info', event.data.data?.payment_info)
                }
            }
            if(event.data.type === "PAYMENT_LISTENER_CHECK") {
                const data = event.data.data
                if(!gotInitMessageRef.current && data?.uuidPayment && data?.paymentProcessToken) {
                    setGotInitMessage(data)
                    updatePaymentProcessInDB({uuid: data.uuidPayment, paymentProcessToken: data.paymentProcessToken, values, overrideFlowName: flowType})
                    setFieldValue('uuidPayment', data.uuidPayment)
                }
                callSiteLog(paymentGotInitMessageLog, event.data, paymentLogGroup)
            }
        }

        const timeoutMaxIdleId = setTimeout(() => {
            if(stepperConfig.currStep === stepOptions.PAYMENT) {
                callSiteLog(paymentTimeoutMessageLog, {}, paymentLogGroup)
                setTimeout(() => window.location.reload(), 100)
            }
        }, 600000)

        window.addEventListener('message', listenToMessages);
        return () => {
            callSiteLog(paymentStopListeningResponseLog, {}, paymentLogGroup)
            window.removeEventListener("message", listenToMessages);
            clearTimeout(timeoutId)
            clearTimeout(timeoutMaxIdleId)
            setSubmitted(false)
        };

    }, []);

    useEffect(() => {
        gotInitMessageRef.current = gotInitMessage;
    }, [gotInitMessage]);

    useEffect(() => {
        if(values.payment_info && !submitted && !verifiedUBT) {
            handlePaymentInfo()
        }
    }, [values.payment_info, submitted, verifiedUBT]);

    useEffect(() => {
        if(values.res && !submitted) {
            console.log('omg submitting!!', values)
            handlePaymentSubmit()
        }
    }, [values.res, submitted]);

    useEffect(() => {
        if(box && language && values && Object.keys(values).length > 0 && !error) {
            setIframeSrc(getIframeSrc())
        }
    }, [box, language, values, userBox]);


    useEffect(() => {
        if(error) {
            callSiteLog(paymentErrorResponseLog, {error, values}, paymentLogGroup)
            setFieldValue('payment_info', null)
            setFieldValue('res', null)
            setIframeSrc(null)
            setSubmitted(false)
            setShowErrorScreen(true)
            setVerifiedUBT(false)
            setGotInitMessage(false)
            // setTimeout(() => setIframeSrc(getIframeSrc()), 10)
        }
    }, [error]);

    useEffect(() => {
        callSiteLog(paymentIframeLog, {iframeSrc, values}, paymentLogGroup)
    }, [iframeSrc]);

    useEffect(() => {
        if(kioskTokenVerifyModalRes && kioskTokenVerifyModalRes.valid) {
            dispatch(updateModalRes({modalName: KIOSK_TOKEN_VERIFY_MODAL, res: null}))
            setVerifiedUBT(true)
            sendMessageToPaymentIframe('continue_execution', {})
        }
    }, [kioskTokenVerifyModalRes]);

    useEffect(() => {
        if(values.cart?.length) {
            handlePurchasePixel(values.cart, pixelEvents.BEGIN_CHECKOUT)
        }
    }, [values.cart]);

    useEffect(() => {
        if(project_type === projectTypes.site && !isKiosk && gotInitMessage) {
            if(values.cart?.length) {
                handleRedirectLink(values.cart[0])
            } else if(stepperConfig?.flags?.membershipTypeSelection) {
                handleRedirectLink(stepperConfig?.flags?.membershipTypeSelection)
            } else if(values?.schedule) {
                handleRedirectLink(values.schedule.box_categories)
            }
        }
    }, [project_type, isKiosk, values?.cart, stepperConfig?.flags?.membershipTypeSelection, values?.schedule, gotInitMessage]);

    const handleRedirectLink = (data) => {
        const redirectLink = data?.redirect_prop?.data?.link ?? null
        if(redirectLink && checkLegitURL(redirectLink)) {
            setTimeout(() => { setFieldValue('redirectLink', redirectLink) }, 100)
        }
    }


    const handlePaymentInfo = () => {
        if(project_type === projectTypes.kiosk && values.payment_info.user_box_token) {
            callSiteLog(openKioskTokenVerifyModal, values, paymentLogGroup)
            dispatch(openModal({modalName: KIOSK_TOKEN_VERIFY_MODAL, props: {},
                onClose: ({fromClose = false}) => {
                    if(fromClose) {
                        setIframeSrc(null)
                        setTimeout(() => setIframeSrc(getIframeSrc()), 100)
                    }
                }}))
        }
    }

    const handlePaymentSubmit = () => {
        setSubmitted(true)
        callSiteLog(paymentSubmitLog, values, paymentLogGroup)
        handleSubmit()
    }

    // name and phone are only for meshulam
    const getUserName = () => {
        const firstName = userBox?.first_name ?? values?.first_name
        const lastName = userBox?.last_name ?? values?.last_name
        if(firstName && lastName) {
            if(firstName.length >= 2 && lastName.length >= 2) {
                return `&name=${firstName} ${lastName}`
            }
        }

        return ''
    }

    const getUserPhone = () => {
        const phone = userBox?.phone ?? values?.phone
        if(phone && isILPhone(phone) && phone.length >= 10) {
            return `&phone=${phone}`
        }
        return ''
    }

    const getQuantity = () => {
        if(values.cart && values.cart.length) {
            const quantities = values.cart.reduce((acc, item) => {
                if(item.quantity > 1) {
                    return {...acc, [item.id]: item.quantity}
                }
                return acc
            }, {})
            if(Object.keys(quantities).length) {
                return `&quantity=${JSON.stringify(quantities)}`
            }
        }
        return ''
    }

    const getStartDates = () => {
        const today = dayjs().format(DB_DATE_FORMAT)
        if(values.cart && values.cart.length) {
            const startDates = values.cart.reduce((acc, item) => {
                if(item?.start && (item.start >= today)) {
                    return {...acc, [item.id]: item.start}
                } else if(item?.start && (item.start < today)) { //just in case the user started the process at date X, and got to the payment step on date X+1 (aka after midnight)
                    window.location.reload()
                }
                return acc
            }, {})
            if(Object.keys(startDates).length) {
                return `&startDates=${JSON.stringify(startDates)}`
            }
        } else if (startOverride) {
            return `&startDates=${JSON.stringify({[idPropOverride ? values[idPropOverride] : values.cart?.id]: startOverride})}`
        }
        return ''
    }

    const getCartIds = () => {
        if(idPropOverride) return [values[idPropOverride]]
        else if(project_type === projectTypes.kiosk) return []
        else {
            return values.cart?.map(item => item.id)
        }
    }

    const getKioskParams = () => {
        let params = ''
        if(project_type === projectTypes.kiosk) {
            params += `&kioskToken=${kioskToken}&visitorUuid=${uuid}`
            if(membershipUser) {
                params += `&membershipUserId=${membershipUser.id}`
            } else if(flowType) {
                params += `&flowType=${flowType}`
            }
        }
        return params
    }

    //this is ONLY for promo check (check validation on child, not on parent)
    const getChildParam = () => {
        let params = ''
        if(values?.child) {
            if(values?.child?.child_id) {
                params += `&userForPromo=${values?.child?.child_id}`
            } else {
                params += `&userForPromo=ignore` //if new child - don't check at all (not even on parent, because the "usage" is on the child)
            }
        }
        return params
    }

    const getSelectedSeriesEvents = () => {
        let params = ''
        if(selectedSeriesEvents && selectedSeriesEvents?.length) {
            params += `&selectedSeriesEvents=${JSON.stringify(selectedSeriesEvents)}`
        }
        return params
    }

    const getFlowType = () => {
        if(flowType) {
            return `&flowType=${flowType}`
        }
        return ''
    }

    const getMembershipUserId = () => {
        if(values?.membershipUserId) {
            return `&membershipUserId=${values?.membershipUserId}`
        }
        return ''
    }

    const getIframeSrc = () => {
        return `${process.env.PAYMENTS_URL}?box_id=${box.id}&lang=${language?.code}&locations_box_id=${values.locations_box_fk ?? location.id}&cart=${JSON.stringify(getCartIds() ?? null)}&token=${token}&hideBack=${hideBack}${getUserName()}${getUserPhone()}${getQuantity()}${getStartDates()}${getKioskParams()}${getChildParam()}${getSelectedSeriesEvents()}${getFlowType()}${getMembershipUserId()}`
    }

    return (
       <FlexColumnSection padding={isMobile ? '0 16px' : 0} align={'start'}>
           <Spin spinning={loading} style={{alignSelf: 'center', justifyContent:'center', flex: 1}}/>
           {iframeSrc &&
           <iframe frameBorder={0}
                   id={'arbox-site-payment-iframe'}
                   style={{width: '100%', flex: 1}}
                   onLoad={() => setLoading(false)}
                   src={iframeSrc}/>}
           {showErrorScreen && <div>{t('payment-error-screen-message')}</div>}
       </FlexColumnSection>
    );
};

export default Payment;